import { LegalBottom } from "@/components/LegalFooter";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  GraduationCap,
  BookOpen,
  Brain,
  Award,
  Clock,
  Euro,
  CheckCircle2,
  ChevronRight,
  Play,
  Users,
  Target,
  Sparkles,
  FileText,
  Shield,
  Home,
  HeartPulse,
  TrendingUp,
  MessageSquare,
  Bot,
  Laptop,
  Calendar,
  Phone,
  Mail,
  ArrowRight,
  Star,
  Zap,
  Lock,
  Download,
  CreditCard,
  ClipboardCheck,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { HeaderLogo, AnimatedTheGoodDealLogo } from "@/components/AnimatedLogo";
import jsPDF from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stripe Payment Link
const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/6oUbJ35VDea10cC8bC2wU0z";

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

// Contact Info
const contactInfo = {
  phone: "01 48 20 98 27",
  email: "contact@jyvis-assurances.com",
  whatsapp: "https://wa.me/message/XE3IM2IGGU5VE1",
};

// Programme de formation
const programmeFormation = [
  {
    semaine: 1,
    titre: "Les Fondamentaux du Secteur de l'Assurance",
    objectif: "Comprendre l'écosystème et les bases juridiques",
    icon: BookOpen,
    color: "bg-blue-500",
    modules: [
      "Présentation du secteur et des acteurs (entreprises d'assurances, intermédiaires)",
      "L'opération d'assurance : Le contrat, la prime, la mutualisation des risques",
      "La classification : Assurances de dommages vs. Assurances de personnes",
      "La relation client et la déontologie (Lutte contre le blanchiment, RGPD)",
    ],
  },
  {
    semaine: 2,
    titre: "Les Assurances de Biens et de Responsabilité",
    objectif: "Maîtriser les techniques de l'IARD",
    icon: Home,
    color: "bg-orange-500",
    modules: [
      "L'appréciation et la sélection des risques",
      "Les contrats habitation, automobile et professionnels",
      "RC Décennale, RC Entreprise et responsabilités",
      "La vie du contrat (tarification, gestion des sinistres, indemnisation)",
    ],
  },
  {
    semaine: 3,
    titre: "Les Assurances de Personnes (Santé & Prévoyance)",
    objectif: "Devenir expert en protection individuelle et collective",
    icon: HeartPulse,
    color: "bg-green-500",
    modules: [
      "Assurance contre les risques corporels (incapacité, invalidité, décès)",
      "L'assurance dépendance",
      "L'assurance complémentaire santé : Fonctionnement et offres",
      "Les contrats collectifs : Assurance de groupe et contrats Madelin",
    ],
  },
  {
    semaine: 4,
    titre: "Assurance Vie & Gestion de Patrimoine",
    objectif: "Conseiller sur les produits d'épargne et de capitalisation",
    icon: TrendingUp,
    color: "bg-purple-500",
    modules: [
      "Prise en compte des besoins du client et catégories de contrats",
      "Euro-croissance, Unités de Compte et diversification",
      "Fiscalité de l'assurance vie et de la capitalisation",
      "Techniques de vente et argumentaire commercial",
    ],
  },
  {
    semaine: 5,
    titre: "Pratique Commerciale, IA & Certification",
    objectif: "Mettre en pratique et valider les acquis",
    icon: Award,
    color: "bg-red-500",
    modules: [
      "La relation client augmentée par l'Intelligence Artificielle",
      "Simulation d'entretiens de courtage avec l'IA",
      "Examen Blanc et révisions",
      "Préparation à la certification ORIAS",
    ],
  },
];

// Fonctionnalités IA
const fonctionnalitesIA = [
  {
    titre: "Tutorat Virtuel 24/7",
    description: "L'IA répond instantanément à vos questions sur le programme. Ex: 'Quelle est la différence entre la RC Décennale et la RC Exploitation ?'",
    icon: MessageSquare,
  },
  {
    titre: "Jeux de Rôle & Mise en situation",
    description: "L'IA joue le rôle d'un client difficile. Vous devez argumenter et l'IA évalue la pertinence de votre réponse commerciale.",
    icon: Users,
  },
  {
    titre: "Générateur d'Études de Cas",
    description: "L'IA génère des profils clients aléatoires et vous devez proposer le contrat le plus adapté à chaque situation.",
    icon: FileText,
  },
  {
    titre: "Aide à la Certification",
    description: "L'IA analyse vos réponses aux tests et propose un feedback personnalisé pour combler vos lacunes avant l'examen.",
    icon: Target,
  },
];

export default function FormationCourtier() {
  const navigate = useNavigate();
  const [activeWeek, setActiveWeek] = useState(1);
  const [showInscription, setShowInscription] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inscriptionForm, setInscriptionForm] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    situation: "",
    adresse: "",
    code_postal: "",
    ville: "",
    date_naissance: "",
  });

  // Génération du Devis PDF
  const generateDevisPDF = (data, inscriptionId) => {
    const doc = new jsPDF();
    const today = new Date();
    const devisNumber = `DEV-FORM-${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}-${inscriptionId.slice(0, 6).toUpperCase()}`;
    
    // Header
    doc.setFillColor(16, 38, 146);
    doc.rect(0, 0, 210, 45, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("JYVIS ASSURANCES", 20, 25);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Formation Courtier en Assurances", 20, 35);
    
    // Devis Info
    doc.setTextColor(249, 115, 22);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("DEVIS", 150, 25);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text(`N° ${devisNumber}`, 150, 33);
    doc.text(`Date: ${today.toLocaleDateString('fr-FR')}`, 150, 40);
    
    // Client Info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("INFORMATIONS STAGIAIRE", 20, 60);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Nom: ${data.prenom} ${data.nom}`, 20, 70);
    doc.text(`Email: ${data.email}`, 20, 77);
    doc.text(`Téléphone: ${data.telephone}`, 20, 84);
    if (data.adresse) doc.text(`Adresse: ${data.adresse}`, 20, 91);
    if (data.code_postal && data.ville) doc.text(`${data.code_postal} ${data.ville}`, 20, 98);
    
    // Formation Details
    doc.setFillColor(245, 245, 245);
    doc.rect(15, 110, 180, 70, 'F');
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("FORMATION", 20, 122);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Expert en Courtage d'Assurances - Niveau 1 (IAS)", 20, 132);
    doc.text("Certification ORIAS", 20, 139);
    
    doc.text("• Durée: 150 heures (5 semaines)", 25, 150);
    doc.text("• Modalité: 100% E-learning", 25, 157);
    doc.text("• Assistant IA AssurIA 24h/24", 25, 164);
    doc.text("• Diplôme + Livret de stage ORIAS inclus", 25, 171);
    
    // Prix
    doc.setFillColor(16, 38, 146);
    doc.rect(15, 190, 180, 25, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("TOTAL TTC", 20, 205);
    doc.setFontSize(18);
    doc.text("700,00 EUR", 150, 205);
    
    // Conditions
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Ce devis est valable 30 jours à compter de sa date d'émission.", 20, 225);
    doc.text("Formation conforme à l'arrêté du 11 juillet 2008 - Article R511-2 du Code des assurances.", 20, 232);
    
    // Footer
    doc.setFillColor(249, 115, 22);
    doc.rect(0, 270, 210, 27, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text("JYVIS Assurances - 47 rue Jean Jaurès, 93200 Saint-Denis", 20, 280);
    doc.text("Tel: 01 48 20 98 27 | Email: contact@jyvis-assurances.com", 20, 287);
    
    doc.save(`Devis_Formation_JYVIS_${data.nom}_${data.prenom}.pdf`);
  };

  // Génération de la Facture PDF
  const generateFacturePDF = (data, inscriptionId) => {
    const doc = new jsPDF();
    const today = new Date();
    const factureNumber = `FAC-FORM-${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}-${inscriptionId.slice(0, 6).toUpperCase()}`;
    
    // Header
    doc.setFillColor(16, 38, 146);
    doc.rect(0, 0, 210, 45, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("JYVIS ASSURANCES", 20, 25);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Formation Courtier en Assurances", 20, 35);
    
    // Facture Info
    doc.setTextColor(34, 197, 94);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("FACTURE", 145, 25);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text(`N° ${factureNumber}`, 145, 33);
    doc.text(`Date: ${today.toLocaleDateString('fr-FR')}`, 145, 40);
    
    // Badge Payé
    doc.setFillColor(34, 197, 94);
    doc.roundedRect(150, 50, 40, 15, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("PAYEE", 160, 60);
    
    // Client Info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("FACTURE A", 20, 60);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`${data.prenom} ${data.nom}`, 20, 70);
    doc.text(`${data.email}`, 20, 77);
    doc.text(`${data.telephone}`, 20, 84);
    if (data.adresse) doc.text(`${data.adresse}`, 20, 91);
    if (data.code_postal && data.ville) doc.text(`${data.code_postal} ${data.ville}`, 20, 98);
    
    // Table Header
    doc.setFillColor(16, 38, 146);
    doc.rect(15, 115, 180, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Description", 20, 123);
    doc.text("Qté", 120, 123);
    doc.text("Prix Unit.", 140, 123);
    doc.text("Total", 175, 123);
    
    // Table Content
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.text("Formation Expert en Courtage - Niveau 1", 20, 135);
    doc.text("(150h - Certification ORIAS)", 20, 142);
    doc.text("1", 123, 135);
    doc.text("700,00 €", 140, 135);
    doc.text("700,00 €", 172, 135);
    
    // Line
    doc.setDrawColor(200, 200, 200);
    doc.line(15, 150, 195, 150);
    
    // Totaux
    doc.setFont("helvetica", "bold");
    doc.text("Sous-total HT:", 130, 165);
    doc.text("583,33 €", 172, 165);
    
    doc.text("TVA (20%):", 130, 175);
    doc.text("116,67 €", 172, 175);
    
    doc.setFillColor(16, 38, 146);
    doc.rect(125, 182, 70, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text("TOTAL TTC:", 130, 191);
    doc.text("700,00 €", 172, 191);
    
    // Payment Info
    doc.setTextColor(34, 197, 94);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Paiement reçu par carte bancaire (Stripe)", 20, 210);
    doc.text(`Date de paiement: ${today.toLocaleDateString('fr-FR')}`, 20, 218);
    
    // Notes
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Formation conforme à l'arrêté du 11 juillet 2008 - Article R511-2 du Code des assurances.", 20, 235);
    doc.text("JYVIS Assurances - SIRET: XXX XXX XXX XXXXX - TVA: FR XX XXX XXX XXX", 20, 242);
    
    // Footer
    doc.setFillColor(249, 115, 22);
    doc.rect(0, 270, 210, 27, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text("JYVIS Assurances - 47 rue Jean Jaurès, 93200 Saint-Denis", 20, 280);
    doc.text("Tel: 01 48 20 98 27 | Email: contact@jyvis-assurances.com", 20, 287);
    
    doc.save(`Facture_Formation_JYVIS_${data.nom}_${data.prenom}.pdf`);
  };

  // Génération du Certificat de Stage PDF
  const generateCertificatPDF = (data, score) => {
    const doc = new jsPDF('landscape');
    const today = new Date();
    const certNumber = `CERT-${today.getFullYear()}-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
    
    // Background gradient effect
    doc.setFillColor(16, 38, 146);
    doc.rect(0, 0, 297, 210, 'F');
    
    // Decorative border
    doc.setDrawColor(249, 115, 22);
    doc.setLineWidth(3);
    doc.rect(10, 10, 277, 190);
    doc.setLineWidth(1);
    doc.rect(15, 15, 267, 180);
    
    // Inner white area
    doc.setFillColor(255, 255, 255);
    doc.rect(20, 20, 257, 170, 'F');
    
    // Header
    doc.setTextColor(16, 38, 146);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("JYVIS ASSURANCES", 148.5, 40, { align: 'center' });
    
    doc.setTextColor(249, 115, 22);
    doc.setFontSize(32);
    doc.text("CERTIFICAT DE REUSSITE", 148.5, 60, { align: 'center' });
    
    doc.setTextColor(16, 38, 146);
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("Formation Expert en Courtage d'Assurances - Niveau 1 (IAS)", 148.5, 75, { align: 'center' });
    
    // Certification text
    doc.setFontSize(12);
    doc.text("Nous certifions que", 148.5, 95, { align: 'center' });
    
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`${data.prenom} ${data.nom}`, 148.5, 115, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.text("a suivi avec succès la formation de 150 heures", 148.5, 130, { align: 'center' });
    doc.text("et a validé l'examen final avec un score de", 148.5, 140, { align: 'center' });
    
    // Score badge
    doc.setFillColor(34, 197, 94);
    doc.roundedRect(128.5, 145, 40, 15, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`${score}%`, 148.5, 156, { align: 'center' });
    
    // Date and signature
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Délivré le ${today.toLocaleDateString('fr-FR')}`, 80, 175);
    doc.text(`N° ${certNumber}`, 80, 182);
    
    doc.text("Le Directeur de la Formation", 217, 175);
    doc.setFont("helvetica", "italic");
    doc.text("JYVIS Assurances", 217, 182);
    
    // ORIAS reference
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("Formation conforme à l'arrêté du 11 juillet 2008 - Article R511-2 du Code des assurances - Recevable par l'ORIAS", 148.5, 195, { align: 'center' });
    
    doc.save(`Certificat_Formation_JYVIS_${data.nom}_${data.prenom}.pdf`);
  };

  const handleInscription = async (e) => {
    e.preventDefault();
    if (!inscriptionForm.prenom || !inscriptionForm.nom || !inscriptionForm.email || !inscriptionForm.telephone) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/formation/inscription`, {
        ...inscriptionForm,
        formation: "Expert en Courtage d'Assurances - Niveau 1",
        tarif: 700,
        created_at: new Date().toISOString(),
      });
      
      const inscriptionId = response.data.inscription?.id || Date.now().toString();
      
      // Générer le devis PDF
      generateDevisPDF(inscriptionForm, inscriptionId);
      
      toast.success("Devis généré ! Redirection vers le paiement...");
      
      // Stocker les infos pour après paiement
      localStorage.setItem('formation_inscription', JSON.stringify({
        ...inscriptionForm,
        inscriptionId,
        date: new Date().toISOString()
      }));
      
      // Redirection vers Stripe après 2 secondes
      setTimeout(() => {
        window.open(STRIPE_PAYMENT_LINK, '_blank');
        setShowInscription(false);
        setInscriptionForm({ 
          prenom: "", nom: "", email: "", telephone: "", situation: "",
          adresse: "", code_postal: "", ville: "", date_naissance: ""
        });
        toast.info("Après votre paiement, revenez sur cette page pour accéder à votre espace formation.");
      }, 2000);
      
    } catch (error) {
      toast.error("Erreur lors de l'inscription. Veuillez réessayer.");
    }
    setLoading(false);
  };

  // Fonction pour générer la facture après paiement (appelée manuellement ou via callback)
  const handleGenerateFacture = () => {
    const savedInscription = localStorage.getItem('formation_inscription');
    if (savedInscription) {
      const data = JSON.parse(savedInscription);
      generateFacturePDF(data, data.inscriptionId);
      toast.success("Facture téléchargée !");
    } else {
      toast.error("Aucune inscription trouvée. Veuillez vous inscrire d'abord.");
    }
  };

  // Fonction pour générer le certificat (après validation QCM)
  const handleGenerateCertificat = (score) => {
    const savedInscription = localStorage.getItem('formation_inscription');
    if (savedInscription && score >= 70) {
      const data = JSON.parse(savedInscription);
      generateCertificatPDF(data, score);
      toast.success("Félicitations ! Certificat téléchargé !");
    } else if (score < 70) {
      toast.error(`Score insuffisant (${score}%). Minimum requis: 70%`);
    } else {
      toast.error("Aucune inscription trouvée.");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#102692] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <HeaderLogo theme="default" size="md" showName={true} dark={true} />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/professionnels" className="text-white/80 hover:text-white transition-colors">
                Professionnels
              </Link>
              <Link to="/particuliers" className="text-white/80 hover:text-white transition-colors">
                Particuliers
              </Link>
              <Link to="/formation-courtier" className="text-white font-semibold border-b-2 border-white pb-1">
                Formation
              </Link>
              <Button 
                onClick={() => setShowInscription(true)}
                className="rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white"
              >
                S'inscrire - 700€
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#102692] via-[#1E40AF] to-[#3730A3] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#F97316] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="bg-[#F97316] text-white mb-4 text-sm px-4 py-1">
                <GraduationCap className="w-4 h-4 mr-2" />
                Formation Certifiante ORIAS
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Devenez<br />
                <span className="text-[#F97316]">Courtier en Assurances</span>
              </h1>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Formation 100% en ligne avec Intelligence Artificielle. 
                Obtenez votre capacité professionnelle ORIAS en 5 semaines.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                  <Clock className="w-5 h-5 text-[#F97316]" />
                  <span>150 heures</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                  <Laptop className="w-5 h-5 text-[#F97316]" />
                  <span>100% E-learning</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                  <Award className="w-5 h-5 text-[#F97316]" />
                  <span>Diplôme certifié</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => setShowInscription(true)}
                  size="lg"
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white text-lg px-8 h-14 rounded-xl"
                >
                  <GraduationCap className="w-5 h-5 mr-2" />
                  S'inscrire maintenant - 700€
                </Button>
                <a
                  href={contactInfo.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white px-6 h-14 rounded-xl font-semibold transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  Être rappelé
                </a>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <p className="text-4xl font-bold text-[#F97316]">700€</p>
                    <p className="text-white/70">Tarif unique tout compris</p>
                  </div>
                  <div className="space-y-4">
                    {[
                      "5 semaines de formation intensive",
                      "150 heures de cours certifiés",
                      "Assistant IA 24h/24",
                      "Diplôme + Livret de stage ORIAS",
                      "Accès à vie aux contenus",
                      "Support personnalisé",
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#F97316] flex-shrink-0" />
                        <span className="text-white/90">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Prérequis et informations */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Users, label: "Prérequis", value: "Aucun - Ouvert à tous", color: "bg-blue-500" },
              { icon: Clock, label: "Durée", value: "5 semaines (150h)", color: "bg-green-500" },
              { icon: Euro, label: "Tarif", value: "700€ TTC", color: "bg-orange-500" },
              { icon: Laptop, label: "Modalité", value: "100% en ligne", color: "bg-purple-500" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="border-0 shadow-lg h-full">
                  <CardContent className="p-6 text-center">
                    <div className={`w-14 h-14 ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <p className="text-sm text-slate-500 mb-1">{item.label}</p>
                    <p className="font-bold text-slate-900">{item.value}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programme pédagogique */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-[#102692]/10 text-[#102692] mb-4">
              <BookOpen className="w-4 h-4 mr-2" />
              Programme Certifié ORIAS
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Programme Pédagogique Détaillé
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              150 heures de formation conforme à l'arrêté du 11 juillet 2008, 
              couvrant l'intégralité des unités obligatoires pour le niveau 1.
            </p>
          </motion.div>

          {/* Timeline des semaines */}
          <div className="flex justify-center gap-2 mb-12 flex-wrap">
            {programmeFormation.map((week) => (
              <button
                key={week.semaine}
                onClick={() => setActiveWeek(week.semaine)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all ${
                  activeWeek === week.semaine
                    ? `${week.color} text-white shadow-lg`
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <span>Semaine {week.semaine}</span>
              </button>
            ))}
          </div>

          {/* Contenu de la semaine active */}
          <AnimatePresence mode="wait">
            {programmeFormation.map((week) => (
              week.semaine === activeWeek && (
                <motion.div
                  key={week.semaine}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="max-w-4xl mx-auto"
                >
                  <Card className="border-0 shadow-xl overflow-hidden">
                    <div className={`${week.color} p-6 text-white`}>
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                          <week.icon className="w-8 h-8" />
                        </div>
                        <div>
                          <p className="text-white/80 text-sm">Semaine {week.semaine}</p>
                          <h3 className="text-2xl font-bold">{week.titre}</h3>
                          <p className="text-white/80 mt-1">{week.objectif}</p>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-8">
                      <div className="grid gap-4">
                        {week.modules.map((module, i) => (
                          <div key={i} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                            <div className={`w-8 h-8 ${week.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <span className="text-white font-bold text-sm">{i + 1}</span>
                            </div>
                            <p className="text-slate-700">{module}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-blue-600" />
                          <p className="text-sm text-blue-800">
                            <strong>Validation :</strong> QCM de fin de semaine (minimum 70% requis pour débloquer la semaine suivante)
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )
            ))}
          </AnimatePresence>
        </div>
      </section>

      {/* Section IA */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-[#F97316] text-white mb-4">
              <Brain className="w-4 h-4 mr-2" />
              Innovation
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              L'Intelligence Artificielle au Coeur de la Formation
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Un assistant IA propriétaire "AssurIA" entraîné sur notre base de données 
              juridiques et commerciales pour une formation unique et ultra-moderne.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {fonctionnalitesIA.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="bg-white/5 border-white/10 h-full hover:bg-white/10 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#F97316] rounded-xl flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-white">{feature.titre}</h3>
                    <p className="text-white/70 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Demo IA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <Card className="bg-white/5 border-white/10 overflow-hidden">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#F97316] to-[#EA580C] rounded-full flex items-center justify-center">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">AssurIA - Votre assistant formation</h3>
                    <p className="text-white/60 text-sm">Disponible 24h/24 pour vous accompagner</p>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 bg-[#F97316] rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white/10 rounded-xl p-3 max-w-lg">
                      <p className="text-white/90 text-sm">
                        Bonjour ! Je suis AssurIA, votre assistant IA dédié à la formation de courtier. 
                        Je peux vous expliquer les concepts, simuler des entretiens clients, ou vous aider 
                        à réviser pour l'examen ORIAS. Comment puis-je vous aider aujourd'hui ?
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-white/50 text-sm">
                    <Sparkles className="w-4 h-4" />
                    <span>Exemple d'interaction avec l'IA de formation</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Certification */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-green-100 text-green-700 mb-4">
              <Award className="w-4 h-4 mr-2" />
              Certification Officielle
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Diplôme et Livret de Stage ORIAS
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Formation recevable par l'ORIAS attestant de 150 heures et de la validation des compétences.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border-2 border-blue-200 h-full">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Validation des Modules</h3>
                  <p className="text-slate-600 text-sm">
                    QCM à la fin de chaque semaine. Minimum 70% requis pour débloquer la suite.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="border-2 border-orange-200 h-full">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Examen Final</h3>
                  <p className="text-slate-600 text-sm">
                    QCM de 100 questions couvrant les 5 unités. Note éliminatoire si inférieure à 70%.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="border-2 border-green-200 h-full">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Documents Officiels</h3>
                  <p className="text-slate-600 text-sm">
                    Diplôme "Certificat de Réussite - Niveau 1 IAS" + Livret de stage 150h pour l'ORIAS.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="mt-12 bg-slate-50 rounded-2xl p-8 text-center">
            <p className="text-slate-700 text-sm italic">
              "Formation certifiante respectant les dispositions de l'article R511-2 du Code des assurances 
              et l'arrêté du 11 juillet 2008."
            </p>
          </div>
        </div>
      </section>

      {/* Espace Stagiaire - Documents */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="bg-[#102692]/10 text-[#102692] mb-4">
              <FileText className="w-4 h-4 mr-2" />
              Espace Stagiaire
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Vos Documents de Formation
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Téléchargez votre facture après paiement et votre certificat après validation de l'examen final.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Devis */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="border-2 border-slate-200 h-full hover:border-[#102692]/30 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Devis</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Généré automatiquement lors de votre inscription.
                  </p>
                  <Button
                    onClick={() => setShowInscription(true)}
                    variant="outline"
                    className="w-full border-blue-200 text-blue-600 hover:bg-blue-50"
                    data-testid="get-devis-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    S'inscrire pour obtenir
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Facture */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="border-2 border-green-200 h-full hover:border-green-300 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Facture</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Disponible après confirmation de votre paiement.
                  </p>
                  <Button
                    onClick={handleGenerateFacture}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    data-testid="download-facture-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger ma facture
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Certificat */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="border-2 border-orange-200 h-full hover:border-orange-300 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-bold text-xl mb-2">Certificat</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Disponible après validation de l'examen (70% min).
                  </p>
                  <Button
                    onClick={() => handleGenerateCertificat(85)}
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white"
                    data-testid="download-certificat-btn"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Obtenir mon certificat
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 max-w-2xl mx-auto"
          >
            <Card className="bg-white border-2 border-[#102692]/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#102692]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="w-6 h-6 text-[#102692]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">Examen Final - QCM de Certification</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      À la fin de votre formation, vous passerez un QCM de 100 questions couvrant les 5 unités.
                      Un score minimum de <span className="font-semibold text-[#F97316]">70%</span> est requis pour obtenir votre certificat.
                    </p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        2 heures
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="w-4 h-4" />
                        100 questions
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        70% requis
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-br from-[#102692] to-[#1E40AF] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à devenir courtier en assurances ?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Rejoignez notre formation et obtenez votre capacité professionnelle ORIAS 
              en seulement 5 semaines. Places limitées !
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => setShowInscription(true)}
                size="lg"
                className="bg-[#F97316] hover:bg-[#EA580C] text-white text-lg px-8 h-14 rounded-xl"
              >
                <GraduationCap className="w-5 h-5 mr-2" />
                S'inscrire maintenant - 700€
              </Button>
              <a
                href={contactInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 h-14 rounded-xl font-semibold transition-colors"
              >
                <Phone className="w-5 h-5" />
                Poser une question
              </a>
            </div>
            <p className="mt-6 text-white/60 text-sm">
              <Phone className="w-4 h-4 inline mr-1" />
              Ou appelez-nous au {contactInfo.phone}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <img src={JYVIS_LOGO} alt="JYVIS" className="h-10 w-auto" />
                <div>
                  <p className="font-bold">JYVIS Assurances</p>
                  <p className="text-sm text-white/60">Formation & Courtage</p>
                </div>
              </div>
              <div className="h-8 w-px bg-white/20" />
              <AnimatedTheGoodDealLogo size="sm" />
            </div>
            <div className="flex items-center gap-6 text-sm text-white/60">
              <a href={`tel:${contactInfo.phone}`} className="hover:text-white flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {contactInfo.phone}
              </a>
              <a href={`mailto:${contactInfo.email}`} className="hover:text-white flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {contactInfo.email}
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-sm text-white/40">
            <LegalBottom />
          </div>
        </div>
      </footer>

      {/* Modal d'inscription */}
      <AnimatePresence>
        {showInscription && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowInscription(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-[#102692] to-[#1E40AF] p-6 text-white rounded-t-2xl">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                    <GraduationCap className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Inscription à la formation</h3>
                    <p className="text-white/80 text-sm">Expert en Courtage - Niveau 1</p>
                  </div>
                </div>
              </div>
              <form onSubmit={handleInscription} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Prénom *</label>
                    <Input
                      placeholder="Votre prénom"
                      value={inscriptionForm.prenom}
                      onChange={(e) => setInscriptionForm(prev => ({ ...prev, prenom: e.target.value }))}
                      required
                      className="h-11"
                      data-testid="formation-prenom"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nom *</label>
                    <Input
                      placeholder="Votre nom"
                      value={inscriptionForm.nom}
                      onChange={(e) => setInscriptionForm(prev => ({ ...prev, nom: e.target.value }))}
                      required
                      className="h-11"
                      data-testid="formation-nom"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email *</label>
                  <Input
                    type="email"
                    placeholder="votre@email.com"
                    value={inscriptionForm.email}
                    onChange={(e) => setInscriptionForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className="h-11"
                    data-testid="formation-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Téléphone *</label>
                  <Input
                    type="tel"
                    placeholder="06 12 34 56 78"
                    value={inscriptionForm.telephone}
                    onChange={(e) => setInscriptionForm(prev => ({ ...prev, telephone: e.target.value }))}
                    required
                    className="h-11"
                    data-testid="formation-telephone"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date de naissance</label>
                  <Input
                    type="date"
                    value={inscriptionForm.date_naissance}
                    onChange={(e) => setInscriptionForm(prev => ({ ...prev, date_naissance: e.target.value }))}
                    className="h-11"
                    data-testid="formation-date-naissance"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Adresse</label>
                  <Input
                    placeholder="Numéro et rue"
                    value={inscriptionForm.adresse}
                    onChange={(e) => setInscriptionForm(prev => ({ ...prev, adresse: e.target.value }))}
                    className="h-11"
                    data-testid="formation-adresse"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Code postal</label>
                    <Input
                      placeholder="93200"
                      value={inscriptionForm.code_postal}
                      onChange={(e) => setInscriptionForm(prev => ({ ...prev, code_postal: e.target.value }))}
                      className="h-11"
                      data-testid="formation-cp"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">Ville</label>
                    <Input
                      placeholder="Saint-Denis"
                      value={inscriptionForm.ville}
                      onChange={(e) => setInscriptionForm(prev => ({ ...prev, ville: e.target.value }))}
                      className="h-11"
                      data-testid="formation-ville"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Situation actuelle</label>
                  <select
                    value={inscriptionForm.situation}
                    onChange={(e) => setInscriptionForm(prev => ({ ...prev, situation: e.target.value }))}
                    className="w-full h-11 px-3 border rounded-lg text-sm"
                    data-testid="formation-situation"
                  >
                    <option value="">Sélectionnez...</option>
                    <option value="salarie">Salarié(e)</option>
                    <option value="demandeur_emploi">Demandeur d'emploi</option>
                    <option value="reconversion">En reconversion</option>
                    <option value="etudiant">Étudiant(e)</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-[#102692]" />
                      <span className="font-semibold text-slate-700">Formation complète</span>
                    </div>
                    <span className="font-bold text-xl text-[#102692]">700€ TTC</span>
                  </div>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>Devis PDF généré automatiquement</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>Paiement sécurisé par Stripe</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>Facture envoyée après paiement</span>
                    </div>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white h-12 font-semibold rounded-xl flex items-center justify-center gap-2"
                  data-testid="formation-submit-btn"
                >
                  {loading ? (
                    "Génération du devis..."
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Générer le devis et payer
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Un devis PDF sera généré avant la redirection vers le paiement sécurisé Stripe.
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
